import ApiClient from './ApiClient'
import * as bb from 'bluebird'

export class Auth extends ApiClient {
  currentUser () {
    return this.get('/auth/currentUser')
  }
}

export class CustomDomains extends ApiClient {
  loadDomains ({ siteId }) {
    return this.post(`/v1/sites/custom-domains/load-domains`, { siteId })
  }

  connectDomain ({ siteId, domain }) {
    return this.post(`/v1/sites/${siteId}/domains`, { siteId, domain })
  }
  
  disconnectDomain ({ siteId, domain }) {
    return this.delete(`/v1/sites/${siteId}/domains/${domain}`)
  }
}

export class Marketplace extends ApiClient {
  templates (params) {
    return this.get('/v1/marketplace/templates', params)
  }

  templateByID (id) {
    return this.get(`/v1/marketplace/templates/${id}`)
  }

  countTemplates (params) {
    return this.get('/v1/marketplace/templates/count', params)
  }
}

export class PopUpMarketplace extends ApiClient {
  popupTemplates (params) {
    return this.get('/v1/popup/marketplace/templates', params)
  }

  popupByID (id) {
    return this.get(`/v1/popup/marketplace/templates/${id}`)
  }

  countPopupTemplate (params) {
    return this.get('/v1/popup/marketplace/templates/count', params)
  }
}

export class Organization extends ApiClient {
  addOrganization (data) {
    return this.post(`/super/create/addOrganization`, data).then(({ data }) => data)
  }
  
  enterpriseOnboarding({ data }) {
    return this.post(`/v1/organization/admin/onboard`, data)
  }

  updateConfiguration(data) {
    return this.post(`/v1/organization/admin/config`, data)
  }

  fetchBrand() {
    return this.get(`/v1/organization/admin/brandFetch`)
  }
  
  inviteITMember(data: {email: string, role: string}[]) {
    return this.post(`/v1/organization/admin/members`, data)
  }
}

export class Super extends ApiClient {
  searchOrganizations ({ orgID }) {
    return this.get('/super/search/organizations', { params: { orgID } })
  }

  searchProjects ({ orgID, projectID }) {
    return this.get(`/super/search/organizations/${orgID}/projects`, { projectID })
  }

  sudo ({ orgID, projectID }) {
    return this.post('/super/sudo', { orgID, projectID })
  }
}

export class V1CRM extends ApiClient {
    formFields () {
      return this.get('/v1/crm/form/fields')
    }

    createFormField (field) {
      return this.post('/v1/crm/form/fields', field)
    }

    createList ({ name }) {
      return this.post('/v1/crm/lists', { name })
    }

    getTags (query) {
      return this.get('/v1/crm/tags', { query })
    }

    createTag ({ name, description }) {
      return this.post('/v1/crm/tags', { name, description })
    }

    searchFiles ({ name, viewable, type }) {
      return this.get('/v1/crm/files', { name, viewable, type })
    }
}

export class V1ECommerce extends ApiClient {
    async listProducts (params) {
      const { data: products } = await this.get('/v1/crm/e-commerce/products', params)
      return products
    }

    async searchProducts (params) {
      const { data: products } = await this.get('/v1/crm/e-commerce/products/search', params)
      return products
    }

    async searchProductsWithSkus (params) {
      return bb.map(await this.searchProducts(params), async (product) => {
        product.skus = await this.listSkusByProductId(product.id, params)
        return product
      })
    }

    async listSkusByProductId (productId, params) {
      const { data: skus } = await this.get(`/v1/crm/e-commerce/products/${productId}/skus`, params)
      return skus
    }

    async listPlansByProductId (productId, params) {
      const { data: plans } = await this.get(`/v1/crm/e-commerce/products/${productId}/plans`, params)
      return plans
    }

    async deleteProductById (productId) {
      const { data: product } = await this.delete(`/v1/crm/e-commerce/products/${productId}`)
      return product
    }

    async createProduct (options) {
      const { data: product } = await this.post('/v1/crm/e-commerce/products', options)
      return product
    }

    async createProductPlan (productId, options) {
      const { data: plan } = await this.post(`/v1/crm/e-commerce/products/${productId}/plans`, options)
      return plan
    }
}

export class V1Sites extends ApiClient {
  all ({ market, archived, limit, offset, dateCreated, dateUpdated, isPublished, search }) {
    return this.get('/v1/sites', { market, archived, limit, offset, dateCreated, dateUpdated, isPublished, search })
  }

  byId (siteId) {
    return this.get(`/v1/sites/${siteId}`)
  }

  updateById (siteId, site) {
    return this.put(`/v1/sites/${siteId}`, site)
  }

  create (foreignData) {
    return this.post('/v1/sites', { foreignData })
  }

  publish (siteId) {
    return this.post(`/v1/sites/${siteId}/publish`)
  }

  unpublish (siteId) {
    return this.post(`/v1/sites/${siteId}/unpublish`)
  }

  fromTemplate (id, foreignData) {
    return this.post(`/v1/sites/fromTemplate/${id}`, { foreignData })
  }

  updateThumbnails (id) {
    return this.post(`/v1/sites/${id}/updateThumbnails`)
  }

  getCategories (id) {
    return this.get(`/v1/sites/super/${id}/categories`)
  }

  updateCategories (id, categories) {
    return this.put(`/v1/sites/super/${id}/categories`, categories)
  }

  enableMarketplace (id, enabled) {
    return this.put(`/v1/sites/super/${id}/marketplace`, { enabled })
  }

  updateCampaignNode (id, campaignData = null) {
    if (campaignData === null) {
      console.debug('no campaign data to update a node with -- skipping')
      return Promise.resolve()
    }
    return this.post(`/v1/sites/${id}/updateCampaignNode`, campaignData)
  }

  analytics ({ siteId, window }) {
    return this.get(`/v1/sites/${siteId}/analytics`, { window })
  }

  regenerateForms ({ siteId }) {
    return this.post(`/v1/sites/${siteId}/regenerateForms`)
  }

  archive (siteId) {
    return this.delete(`/v1/sites/${siteId}`)
  }

  restore (siteId) {
    return this.get(`/v1/sites/${siteId}/restore`)
  }

  updatePage (page) {
    const { siteId, id } = page
    return this.put(`/v1/sites/${siteId}/pages/${id}`, page)
  }

  clonePage ({ siteId, id }) {
    return this.post(`/v1/sites/${siteId}/pages/${id}/clone`)
  }

  insertPage ({ siteId, pageProps }) {
    return this.post(`/v1/sites/${siteId}/insertPage`, pageProps)
  }

  savePage ({ siteId, id, pageProps }) {
    return this.post(`/v1/sites/${siteId}/pages/${id}/save`, pageProps)
  }

  deletePage ({ siteId, id }) {
    return this.delete(`/v1/sites/${siteId}/pages/${id}`)
  }

  replacePage ({ siteId, id, pageProps }) {
    return this.post(`/v1/sites/${siteId}/pages/${id}/replacePage`, pageProps)
  }

  createPage (page) {
    const { siteId } = page
    return this.post(`/v1/sites/${siteId}/pages`, page)
  }
}

export class V1PopupSite extends ApiClient {
  all ({ market, archived, limit, offset, dateCreated, dateUpdated, isPublished, search }) {
    return this.get('/v1/popup/marketplace/templates', { market, archived, limit, offset, dateCreated, dateUpdated, isPublished, search })
  }

  byId (popUpId) {
    return this.get(`/v1/popup/${popUpId}`)
  }

  updateById (popUpId, popup) {
    return this.put(`/v1/popup/${popUpId}`, popup)
  }

  create (foreignData) {
    return this.post('/v1/popup', { foreignData })
  }

  publish (siteId) {
    return this.post(`/v1/popup/sites/${siteId}/publish`)
  }

  unpublish (siteId) {
    return this.post(`/v1/popup/sites/${siteId}/unpublish`)
  }

  fromPopup (popUpId, foreignData) {
    return this.post(`/v1/popup/${popUpId}/clone`, { foreignData })
  }

  updateThumbnails (id) {
    return this.post(`/v1/popup/sites/${id}/updateThumbnails`)
  }

  archive (siteId) {
    return this.delete(`/v1/popup/sites/${siteId}`)
  }

  restore (siteId) {
    return this.get(`/v1/popup/sites/${siteId}/restore`)
  }
}

export class V1Stripe extends ApiClient {
  connect ({ code }) {
    return this.post('/v1/stripe/connect', { code }).then(({ data }) => data)
  }

  account () {
    return this.get('v1/stripe/api/account').then(({ data }) => data)
  }

  getAllProducts ({ limit, shippable = false }) {
    return this.get('/v1/stripe/api/products', { params: { limit, shippable } }).then(({ data }) => data)
  }

  createProduct (product) {
    return this.post('/v1/stripe/api/products', product).then(({ data }) => data)
  }

  createProductSKU (productId, sku) {
    return this.post(`/v1/stripe/api/products/${productId}/skus`, sku).then(({ data }) => data)
  }

  updateProduct (productId, data) {
    return this.post(`/v1/stripe/api/products/${productId}`, data).then(({ data }) => data)
  }

  deleteProduct (productId) {
    return this.delete(`/v1/stripe/api/products/${productId}`).then(({ data }) => data)
  }
}

export default new ApiClient()